/**
 * Shared fail-closed pick-list signal (DNKADM-0008 U1; v1 wrappers retired
 * DNKADM-0074 U3).
 *
 * This module is now the home of the **distinct fail-closed signal** every live
 * reference-data loader reuses — the {@link PickListError} class, its
 * {@link PickListKind} discriminant, and the realm-safe {@link isPickListError}
 * guard. It carries NO endpoint wrappers of its own anymore.
 *
 * **History (the v1 cutover).** This file originally also exported four v1
 * `/admin/**` reference-data wrappers — `getSquareCatalog` (`/admin/square/catalog`),
 * `getGhlMembershipTiers`, `getGhlCreditBuckets`, `getGhlContactFields`
 * (`/admin/ghl/*`). The v2 Project-Independence rebuild (DNKADM-0049–0063) turned
 * those GHL/Square-derived lists into owned v2 entities, and the live pickers moved
 * onto the v2 wrappers that reuse this same signal:
 *
 *  - credit buckets   → `getCreditBucketOptions` / `useCreditBucketPickList`
 *                       (`creditBuckets.ts`, `/v2/admin/credit-buckets`, §5.4)
 *  - membership plans → `getMembershipPlanOptions` / `useMembershipPlanPickList`
 *                       (`membershipPlans.ts`, `/v2/admin/membership-plans`, §5.9)
 *  - service taxonomy → `getServiceCategoryOptions` (`serviceCategories.ts`)
 *  - staff/resources/locations → `staff.ts` / `resources.ts` / the services feature
 *
 * The v1 wrappers' last consumers (the GHL `contact-fields` pickers) were the v1
 * custom-fields dialogs, retired in DNKADM-0074 U2. With no remaining live consumer
 * the four wrappers + their parse helpers were deleted here in U3; the fail-closed
 * **signal** they introduced is kept because the whole v2 pick-list fleet depends on
 * it.
 *
 * Contract: `docs/design/admin-ui-technical-spec.md` §1.2 (fail-closed), §1.3
 * (error model). The fail-closed posture is enforced in each wrapper's own module;
 * `usePickList` (`@/hooks/usePickList`) switches on {@link isPickListError} to render
 * `error + Retry` (never a partial/empty-as-success list).
 */

/**
 * The distinct fail-closed signal. Thrown by every pick-list wrapper (the v2
 * `creditBuckets`/`membershipPlans`/`serviceCategories`/`staff`/`resources`/
 * `memberProfileFields` loaders + the services-feature location loader) when the
 * live list cannot be loaded COMPLETELY — an upstream/network failure, or a `200`
 * whose body is not the expected array. The loader switches on this to render
 * `error + Retry`; it is intentionally NOT an empty array, so a failure can never
 * masquerade as "loaded, empty".
 */
export class PickListError extends Error {
  /** A stable discriminant for `instanceof`-free checks (e.g. across realms). */
  readonly isPickListError = true;
  /** Which pick-list failed — for the error copy / diagnostics. */
  readonly picklist: PickListKind;
  /** The originating error (an `ApiError`, a malformed-body marker, …), if any. */
  override readonly cause: unknown;

  constructor(picklist: PickListKind, cause: unknown) {
    super(`Failed to load pick-list "${picklist}"`);
    this.name = 'PickListError';
    this.picklist = picklist;
    this.cause = cause;
  }
}

/** Which live list a {@link PickListError} refers to. */
export type PickListKind =
  // v2 credit-bucket loader (DNKADM-0052) — `creditBuckets.ts`
  // (`getCreditBucketOptions` / `useCreditBucketPickList`). Retains the historical
  // `ghl-` discriminant name for stability; the wire surface is `/v2/admin/credit-buckets`.
  | 'ghl-credit-buckets'
  // v2 catalog-taxonomy loaders (DNKADM-0052) reuse this distinct fail-closed
  // signal via the shared `PickListError`; the wrappers themselves live in their
  // own modules (`serviceCategories.ts` / `creditBuckets.ts`).
  | 'service-categories'
  // v2 staff entity pick-list (DNKADM-0053) — same shared fail-closed signal; the
  // wrapper (`getStaffOptions` / `useStaffPickList`) lives in `staff.ts`.
  | 'staff'
  // v2 resource entity pick-list (DNKADM-0054) — same shared fail-closed signal;
  // `getResourceOptions` lives in `resources.ts`; ServiceDetail passes it to
  // `usePickList`.
  | 'resources'
  // v2 owned-location pick-list (DNKADM-0055) — same shared fail-closed signal; the
  // service form's local `getLocationOptions` wrapper lives in the services feature.
  | 'locations'
  // v2 membership-plan pick-list (DNKADM-0056) — same shared fail-closed signal; the
  // wrapper (`getMembershipPlanOptions` / `useMembershipPlanPickList`) lives in
  // `membershipPlans.ts`. Consumed by daily caps (0057) + members (0059).
  | 'membership-plans'
  // v2 member-profile-fields registry (DNKADM-0058) — same shared fail-closed signal;
  // the wrapper (`getMemberProfileFields` / `useMemberProfileFieldList`) lives in
  // `memberProfileFields.ts`. Consumed by the member profile editor (0059).
  | 'member-profile-fields'
  // v2 payment-methods pick-list (DNKADM-0091, §E / DNKAPI-0171) — same shared
  // fail-closed signal; the wrapper (`getPaymentMethodOptions` /
  // `usePaymentMethodPickList`) lives in `paymentMethods.ts`. Forward-compatible: an
  // absent endpoint degrades to Cash-only in the wrapper (NOT a PickListError);
  // genuine failures still raise this. Consumed by the Add-Member dialog (DNKADM-0092).
  | 'payment-methods';

/**
 * True for a {@link PickListError} regardless of realm — prefer this over a bare
 * `instanceof` at consumer boundaries (the loader uses it to detect fail-closed).
 */
export function isPickListError(error: unknown): error is PickListError {
  return (
    error instanceof PickListError ||
    (typeof error === 'object' &&
      error !== null &&
      (error as { isPickListError?: unknown }).isPickListError === true)
  );
}
